import React from 'react';
import Space from 'antd/lib/space';
import {
    AppComponentProps,
    QuickStartCISetupStep,
    QuickStartCISetupStepType,
    QuickStartCISetupViewProps,
} from '../../../typescriptHelpers';
import { HelpTip, InfoTip } from '../../setup/shared';
import {
    CheckOutlined,
    PullRequestOutlined,
    SyncOutlined,
    DownloadOutlined,
    LinkOutlined,
    CaretRightOutlined,
} from '@ant-design/icons';
import Button from 'antd/lib/button';
import Collapse from 'antd/lib/collapse';
import Text from 'antd/lib/typography/Text';
import FormItem from 'antd/lib/form/FormItem';
import useForm from 'antd/lib/form/hooks/useForm';
import Form, { List } from 'antd/lib/form/Form';
import Checkbox from 'antd/lib/checkbox/Checkbox';
import { antGrey } from '../../styles/globalStyles';
import { useGenerateQuickStartCIFile } from '../hooks/useGenerateQuickStartCIFiles';
import { useSelector } from 'react-redux';
import { RootState } from '../../../reducers/reducers';
import { selectAppPlatform, selectQuickStartCIStepData } from '../../../selectors/selectors';
import { ColoredButton, TrackedColoredButton } from '../ColoredButton';
import Spin from 'antd/lib/spin';
import { useOpenPRForQuickStartCIFiles } from '../hooks/useOpenPRForQuickStartCIFiles';
import { globalMessage } from '../../../utils/globalMessage';
import { AppPlatform, IntegrationType, QuickStartCIFileType } from '../../../types';
import { useDownloadFiles } from '../hooks/useDownloadFiles';
import * as AnalyticEvents from '../../../utils/analytics';
import TrackedButton from '../../../helpers/TrackedButton';
import { displayNameForQuickStartCIFileType, identifierForAppPlatform } from '../../../helpers/displayHelpers';
import Link from 'antd/lib/typography/Link';

const SetupViewComponent: React.FC<QuickStartCISetupViewProps> = ({ appId, orgId }) => {
    const stepData = useSelector((state: RootState) =>
        selectQuickStartCIStepData(state, appId, QuickStartCISetupStepType.ScanDetailsConfirmation),
    );

    const platform = useSelector((state: RootState) => selectAppPlatform(state, appId));
    const [isDownloading, downloadingError, downloadFiles] = useDownloadFiles();

    const [lastCreatedPRUrl, setLastCreatedPRUrl] = React.useState<string | undefined>(undefined);

    const [generatedFilesResult, isLoading, error, generateQuickStartCIFiles] = useGenerateQuickStartCIFile(
        orgId,
        appId,
    );

    const [openPRResult, openPRIsLoading, openPRError, openPRForQuickStartCIFiles] = useOpenPRForQuickStartCIFiles(
        orgId,
        appId,
    );

    const [form] = useForm();

    const uploadSecretsStepData = useSelector((state: RootState) =>
        selectQuickStartCIStepData(state, appId, QuickStartCISetupStepType.UploadSecrets),
    );

    const generateFiles = React.useCallback(() => {
        generateQuickStartCIFiles({
            ...stepData?.scanConfirmedResult,
            appId,
            orgId,
            ciProvider: IntegrationType.GitHubCI,
            provisioningProfileId: uploadSecretsStepData?.provisioningProfileId,
        });
    }, [stepData?.scanConfirmedResult, appId, orgId, uploadSecretsStepData?.provisioningProfileId]);

    React.useEffect(() => {
        // on mount load the files if scanConfirmedResult is available
        if (stepData?.scanConfirmedResult) {
            generateFiles();
        }
    }, [stepData?.scanConfirmedResult, generateFiles]);

    React.useEffect(() => {
        if (openPRError) {
            globalMessage.error(openPRError.message);
        }
    }, [openPRError]);

    React.useEffect(() => {
        if (downloadingError) {
            globalMessage.error(downloadingError.message);
        }
    }, [downloadingError]);

    React.useEffect(() => {
        if (openPRResult) {
            globalMessage.success('Pull request created successfully!');
            setLastCreatedPRUrl(openPRResult.prUrl);
        }
    }, [openPRResult]);

    React.useEffect(() => {
        if (generatedFilesResult) {
            form.setFieldsValue({
                files: generatedFilesResult.resultFiles.map((f) => ({
                    ...f,
                    selected: true,
                })),
            });
        }
    }, [generatedFilesResult]);

    if (isLoading || !!error) {
        return (
            <div
                style={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    flex: 1,
                    minHeight: 200,
                }}
            >
                {isLoading && <Spin size="default" tip="Loading files" />}
                {error && !isLoading && (
                    <div
                        style={{
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'center',
                            alignItems: 'center',
                            gap: 12,
                        }}
                    >
                        <Text type="danger">{error.message}</Text>
                        <Button
                            ghost
                            icon={<SyncOutlined />}
                            onClick={() => {
                                generateFiles();
                            }}
                        >
                            Retry
                        </Button>
                    </div>
                )}
            </div>
        );
    }

    return (
        <Form
            form={form}
            initialValues={{
                files: [],
            }}
            onValuesChange={() => {
                setLastCreatedPRUrl(undefined);
            }}
            onFinish={(values) => {
                const selectedFiles = values.files.filter((f) => f.selected);
                openPRForQuickStartCIFiles(selectedFiles);
            }}
            autoComplete="off"
        >
            <List name="files">
                {(fields) => (
                    <>
                        {fields.map(({ key, name, ...restField }) => {
                            const fileType = form.getFieldValue(['files', key, 'fileType']);

                            return (
                                <div
                                    key={key}
                                    style={{
                                        display: 'flex',
                                        alignItems: 'baseline',
                                        gap: 12,
                                        marginBottom: 12,
                                    }}
                                >
                                    <FormItem
                                        key={key}
                                        {...restField}
                                        name={[name, 'selected']}
                                        valuePropName="checked"
                                    >
                                        <Checkbox
                                            style={{
                                                width: '100%',
                                            }}
                                            className="quickstart-ci-checkbox"
                                        ></Checkbox>
                                    </FormItem>
                                    <Collapse
                                        bordered={false}
                                        style={{
                                            flex: 1,
                                        }}
                                        className="setup-steps-accordion"
                                        expandIcon={({ isActive }) => (
                                            <CaretRightOutlined
                                                style={{
                                                    color: antGrey.grey6,
                                                    fontSize: 22,
                                                }}
                                                rotate={isActive ? 90 : 0}
                                            />
                                        )}
                                    >
                                        <Collapse.Panel
                                            key={key}
                                            style={{ padding: 6 }}
                                            header={
                                                <Space size={12}>
                                                    <Text
                                                        style={{
                                                            color: antGrey.grey8,
                                                            fontSize: 14,
                                                            fontWeight: 600,
                                                        }}
                                                    >
                                                        {displayNameForQuickStartCIFileType(fileType)}
                                                    </Text>
                                                </Space>
                                            }
                                        >
                                            {descriptionForQuickStartCIFileType(fileType, platform)}
                                        </Collapse.Panel>
                                    </Collapse>
                                </div>
                            );
                        })}
                    </>
                )}
            </List>
            <Space
                style={{
                    paddingTop: 12,
                }}
                size={12}
                align="center"
            >
                <FormItem shouldUpdate noStyle>
                    {(form) => {
                        const fileCount = form.getFieldValue('files').filter((f) => f.selected).length;
                        return lastCreatedPRUrl ?
                                <ColoredButton
                                    type="primary"
                                    icon={<LinkOutlined />}
                                    href={lastCreatedPRUrl}
                                    target="_blank"
                                >
                                    View pull request for {fileCount} file{fileCount !== 1 ? 's' : ''}
                                </ColoredButton>
                            :   <TrackedColoredButton
                                    eventName={AnalyticEvents.Event.ClickQuickStartCICta}
                                    extraProperties={{
                                        ctaType: AnalyticEvents.QuickStartCICTAs.OpenPR,
                                    }}
                                    type="primary"
                                    htmlType="submit"
                                    disabled={fileCount <= 0}
                                    icon={<PullRequestOutlined />}
                                    loading={openPRIsLoading}
                                >
                                    Open pull request for {fileCount} file{fileCount !== 1 ? 's' : ''}
                                </TrackedColoredButton>;
                    }}
                </FormItem>

                <FormItem shouldUpdate noStyle>
                    {(form) => {
                        const files = form.getFieldValue('files').filter((f) => f.selected);
                        const fileCount = files.length;

                        return (
                            <TrackedButton
                                eventName={AnalyticEvents.Event.ClickQuickStartCICta}
                                extraProperties={{
                                    ctaType: AnalyticEvents.QuickStartCICTAs.DownloadFiles,
                                }}
                                onClick={() => {
                                    downloadFiles(files);
                                }}
                                size="large"
                                disabled={fileCount <= 0}
                                icon={<DownloadOutlined />}
                                loading={isDownloading}
                            >
                                Download {fileCount} file{fileCount !== 1 ? 's' : ''}
                            </TrackedButton>
                        );
                    }}
                </FormItem>
            </Space>
        </Form>
    );
};

const InfoViewComponent: React.FC<AppComponentProps> = () => {
    return (
        <Space direction="vertical">
            <InfoTip>
                Download your generated workflow configuration files, or open a pull request to finish setting up your
                workflow.
            </InfoTip>
            <HelpTip>
                Need more help with your mobile release process?
                <br />
                <br />
                <ul>
                    <li>
                        <Button
                            type="link"
                            style={{ padding: 0, display: 'inline', lineHeight: 'revert' }}
                            href="https://www.runway.team"
                            target="_blank"
                        >
                            Learn more
                        </Button>{' '}
                        about the Runway release management platform
                    </li>
                    <li>
                        <Button
                            type="link"
                            style={{ padding: 0, display: 'inline', lineHeight: 'revert' }}
                            href="https://www.runway.team/demo"
                            target="_blank"
                        >
                            Schedule a demo
                        </Button>
                    </li>
                </ul>
            </HelpTip>
        </Space>
    );
};

export const DownloadFiles: QuickStartCISetupStep = {
    icon: () => CheckOutlined,
    headerTitle: 'Ready! We’ve prepared your...',
    SetupViewComponent,
    InfoViewComponent,
    isCompleteSelector: () => {
        return true;
    },
};

const descriptionForQuickStartCIFileType = (fileType: QuickStartCIFileType, platform: AppPlatform) => {
    switch (fileType) {
        case QuickStartCIFileType.Appfile:
            return (
                <Text>
                    Appfiles are used by fastlane to define global variables that can be used across all fastlane
                    scripts. It’s good practice to define your app’s {identifierForAppPlatform(platform)} in your
                    Appfile.
                    <br />
                    <br />
                    You can read more about fastlane Appfiles in fastlane’s{' '}
                    <Link href="https://docs.fastlane.tools/advanced/Appfile/#appfile" target="_blank">
                        docs
                    </Link>
                    .
                </Text>
            );
        case QuickStartCIFileType.Fastfile:
            return (
                <Text>
                    fastlane is a tool that simplifies the execution of common mobile build and deploy tasks. The
                    Fastfile defines custom combinations of actions that can be called with fastlane/ Runway has already
                    configured your Fastfile with a set of actions to easily increment your build number, build, sign,
                    and upload your app to the app store, but you can continue to expand your Fastfile to suit your
                    team’s needs.
                    <br />
                    <br />
                    Learn more about what’s possible with fastlane from their{' '}
                    <Link href="https://docs.fastlane.tools/" target="_blank">
                        docs
                    </Link>
                    .
                </Text>
            );
        case QuickStartCIFileType.Gemfile:
            return (
                <Text>
                    A Gemfile defines your project’s Ruby dependencies – it’s needed to install Ruby gems such as
                    fastlane.
                </Text>
            );
        case QuickStartCIFileType.Pluginfile:
            return (
                <Text>
                    Pluginfiles are used by fastlane to install fastlane{' '}
                    <Link href="https://docs.fastlane.tools/plugins/available-plugins/" target="_blank">
                        plugins
                    </Link>{' '}
                    – handy third party fastlane action extensions that expand fastlane’s capabilities.
                </Text>
            );
        case QuickStartCIFileType.GHWorkflowFile:
            return (
                <Text>
                    Your workflow file tells GitHub Actions when and how to perform your build. Runway has configured it
                    out-of-the-box to install your project dependencies, load signing secrets from GitHub’s encrypted
                    secrets into the build, and call fastlane to build and deploy your app. Your workflow is configured
                    with a manual trigger, but you can easily configure it to run on event triggers like pushes to a
                    branch or even on a defined schedule.
                    <br />
                    <br />
                    Read more about how to further configure your GitHub Actions workflow file{' '}
                    <Link href="https://docs.github.com/en/actions/using-workflows/about-workflows" target="_blank">
                        here
                    </Link>
                    .
                </Text>
            );
    }
};
